import React, { useState, useCallback, useEffect, useRef } from 'react';

import EmailStep from './EmailStep';
import PasswordStep from './PasswordStep';

const STEPS = {
  EMAIL: 'email',
  PASSWORD: 'password',
};

const CustomLoginFlow = ({
  onSubmit,
  onGoogleSignIn,
  error,
  isSubmitting,
  onClearError,
  showGoogleLogin = true,
  isWidgetRendered = false,
  loginPrefill = null,
}) => {
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const syncedPrefillEmailRef = useRef(null);
  const rememberMeSyncedRef = useRef(false);

  useEffect(() => {
    if (!loginPrefill) return;

    const { email: oktaEmail, rememberMe: oktaRememberMe } = loginPrefill;

    if (oktaEmail && oktaEmail !== syncedPrefillEmailRef.current) {
      setEmail(oktaEmail);
      syncedPrefillEmailRef.current = oktaEmail;
    }
    if (typeof oktaRememberMe === 'boolean' && !rememberMeSyncedRef.current) {
      setRememberMe(oktaRememberMe);
      rememberMeSyncedRef.current = true;
    }
  }, [loginPrefill]);

  const handleEmailChange = useCallback((value) => {
    setEmail(value);
    onClearError?.();
  }, [onClearError]);

  const handleEmailContinue = useCallback(() => {
    onClearError?.();
    setStep(STEPS.PASSWORD);
  }, [onClearError]);

  const handlePasswordSubmit = useCallback((password) => {
    onSubmit?.(email, password, rememberMe);
  }, [email, rememberMe, onSubmit]);

  const handleChangeEmail = useCallback(() => {
    setStep(STEPS.EMAIL);
    onClearError?.();
  }, [onClearError]);

  const handleGoogleSignIn = useCallback(() => {
    onClearError?.();
    onGoogleSignIn?.();
  }, [onClearError, onGoogleSignIn]);

  if (step === STEPS.EMAIL) {
    return (
      <EmailStep
        email={email}
        onEmailChange={handleEmailChange}
        onContinue={handleEmailContinue}
        onGoogleSignIn={handleGoogleSignIn}
        error={error}
        isSubmitting={isSubmitting}
        rememberMe={rememberMe}
        onRememberMeChange={setRememberMe}
        showGoogleLogin={showGoogleLogin}
        isWidgetRendered={isWidgetRendered}
      />
    );
  }

  return (
    <PasswordStep
      email={email}
      onChangeEmail={handleChangeEmail}
      onPasswordSubmit={handlePasswordSubmit}
      onGoogleSignIn={handleGoogleSignIn}
      error={error}
      isSubmitting={isSubmitting}
      rememberMe={rememberMe}
      onRememberMeChange={setRememberMe}
      showGoogleLogin={showGoogleLogin}
      isWidgetRendered={isWidgetRendered}
    />
  );
};

export default CustomLoginFlow;
